import { useState, useEffect, useRef, useCallback } from "react";
import { alarmRecords } from "@/mocks/alarm";

const aiReportText = `【AI告警智能分析报告 - 2026年4月10日】

一、告警总体概况
今日共产生告警 ${8} 条，其中紧急告警 1 条（占比12.5%），严重告警 2 条（占比25%），警告告警 3 条（占比37.5%），提示告警 2 条（占比25%）。未处理告警 3 条，需立即关注。

二、AI智能分类分析
▶ 安全类告警（泄漏/压力）：共2条，占比25%，风险等级：高
  - 海淀区中关村南大街15号燃气泄漏报警，浓度达25.3% LEL，已超爆炸下限2.5倍
  - 东城区鼓楼东大街56号压力超标，已安排工程师现场排查

▶ 设备类告警（通信/设备异常）：共2条，占比25%，风险等级：中
  - 通信中断类告警已全部处理，设备恢复正常
  - 建议对信号弱区域设备进行网络优化

▶ 用户类告警（欠费/安检逾期）：共3条，占比37.5%，风险等级：低-中
  - 欠费预警用户1人，余额¥89.50，建议立即发送充值提醒短信
  - 安检逾期用户1人，已逾期54天，建议优先安排安检

▶ 保险类告警：共1条，占比12.5%，风险等级：低
  - 1名用户保险将于90天后到期，建议提前推送续保提醒

三、AI风险预测
基于历史数据分析，预测未来7天：
- 泄漏类告警概率：12%（季节性偏高，春季管道热胀冷缩）
- 欠费预警数量：预计新增8-12条
- 设备离线风险：3台设备信号持续偏弱，建议提前维护

四、AI处置建议
1. 【紧急】立即派员处理海淀区燃气泄漏告警，同时通知用户疏散
2. 【高优先】对3条未处理告警在2小时内完成响应
3. 【中优先】对欠费用户批量发送短信催缴，预计可回收¥2,400
4. 【低优先】安排安检逾期用户本周内完成安检

五、系统优化建议
建议将泄漏报警阈值从10% LEL调整为8% LEL，可提前15-20分钟预警，有效降低安全风险。`;

const levelTrend = [
  { day: "4/4", urgent: 0, serious: 1, warning: 2, info: 1 },
  { day: "4/5", urgent: 1, serious: 0, warning: 3, info: 2 },
  { day: "4/6", urgent: 0, serious: 2, warning: 1, info: 0 },
  { day: "4/7", urgent: 0, serious: 1, warning: 2, info: 1 },
  { day: "4/8", urgent: 1, serious: 1, warning: 2, info: 1 },
  { day: "4/9", urgent: 0, serious: 1, warning: 1, info: 2 },
  { day: "4/10", urgent: 1, serious: 2, warning: 3, info: 2 },
];

const riskUsers = [
  { name: "李秀梅", risk: 92, issues: ["燃气泄漏", "欠费预警", "安检逾期"], level: "高危" },
  { name: "老北京涮肉馆", risk: 68, issues: ["压力异常"], level: "中危" },
  { name: "北京华联超市", risk: 45, issues: ["流量异常"], level: "低危" },
  { name: "张建国", risk: 22, issues: ["保险到期"], level: "关注" },
];

const typeDistribution = [
  { type: "泄漏报警", count: 1, color: "#ef4444", pct: 12.5 },
  { type: "欠费预警", count: 1, color: "#f59e0b", pct: 12.5 },
  { type: "压力异常", count: 1, color: "#f97316", pct: 12.5 },
  { type: "通信中断", count: 1, color: "#fb923c", pct: 12.5 },
  { type: "安检逾期", count: 1, color: "#fbbf24", pct: 12.5 },
  { type: "流量异常", count: 1, color: "#eab308", pct: 12.5 },
  { type: "保险到期", count: 1, color: "#14b8a6", pct: 12.5 },
  { type: "设备异常", count: 1, color: "#f97316", pct: 12.5 },
];

export default function AlarmAIAnalysis() {
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [activeView, setActiveView] = useState<"overview" | "category" | "trend" | "risk">("overview");
  const [exportModal, setExportModal] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const trendCanvasRef = useRef<HTMLCanvasElement>(null);
  const donutCanvasRef = useRef<HTMLCanvasElement>(null);
  const heatmapCanvasRef = useRef<HTMLCanvasElement>(null);

  const total = alarmRecords.length;

  const drawTrend = useCallback(() => {
    const canvas = trendCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const padL = 28, padR = 10, padT = 16, padB = 24;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const maxV = Math.max(...levelTrend.map(d => d.urgent + d.serious + d.warning + d.info)) + 1;

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.05)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(String(Math.round(maxV - (maxV / 4) * i)), padL - 3, y + 3);
    }

    const colors = { urgent: "#ef4444", serious: "#f97316", warning: "#f59e0b", info: "#14b8a6" };
    const keys = ["info", "warning", "serious", "urgent"] as const;

    // Stacked bars
    levelTrend.forEach((d, i) => {
      const x = padL + (i / (levelTrend.length - 1)) * chartW;
      const barW = chartW / levelTrend.length * 0.5;
      let stackY = padT + chartH;
      keys.forEach(k => {
        const v = d[k];
        if (v === 0) return;
        const barH = (v / maxV) * chartH;
        stackY -= barH;
        ctx.fillStyle = colors[k];
        ctx.beginPath();
        ctx.roundRect(x - barW / 2, stackY, barW, barH, k === "urgent" ? 2 : 0);
        ctx.fill();
      });
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.day, x, H - 6);
    });

    // Trend line (total)
    ctx.beginPath();
    levelTrend.forEach((d, i) => {
      const total = d.urgent + d.serious + d.warning + d.info;
      const x = padL + (i / (levelTrend.length - 1)) * chartW;
      const y = padT + chartH - (total / maxV) * chartH;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "rgba(232,83,26,0.5)";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  const drawDonut = useCallback(() => {
    const canvas = donutCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const cx = W / 2, cy = H / 2, r = Math.min(W, H) / 2 - 8;
    const levels = [
      { label: "紧急", count: 1, color: "#ef4444" },
      { label: "严重", count: 2, color: "#f97316" },
      { label: "警告", count: 3, color: "#f59e0b" },
      { label: "提示", count: 2, color: "#14b8a6" },
    ];
    const totalCount = levels.reduce((s, l) => s + l.count, 0);
    let startAngle = -Math.PI / 2;
    levels.forEach(l => {
      const angle = (l.count / totalCount) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, startAngle + angle);
      ctx.closePath();
      ctx.fillStyle = l.color;
      ctx.fill();
      startAngle += angle;
    });
    // Inner hole
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.55, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();
    // Center text
    ctx.fillStyle = "#374151";
    ctx.font = "bold 16px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(String(totalCount), cx, cy + 4);
    ctx.fillStyle = "#9ca3af";
    ctx.font = "9px sans-serif";
    ctx.fillText("总告警", cx, cy + 16);
  }, []);

  const drawHeatmap = useCallback(() => {
    const canvas = heatmapCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    // 7 days x 24 hours heatmap
    const days = ["一", "二", "三", "四", "五", "六", "日"];
    const cellW = (W - 24) / 24;
    const cellH = (H - 16) / 7;
    const heatData = Array.from({ length: 7 }, (_, d) =>
      Array.from({ length: 24 }, (_, h) => {
        const base = (h >= 7 && h <= 9) || (h >= 18 && h <= 20) ? 3 : 1;
        return Math.random() > 0.7 ? Math.floor(Math.random() * base + 1) : 0;
      })
    );
    heatData.forEach((row, d) => {
      row.forEach((v, h) => {
        const x = 24 + h * cellW;
        const y = d * cellH;
        const alpha = v === 0 ? 0.05 : v === 1 ? 0.3 : v === 2 ? 0.6 : 0.9;
        ctx.fillStyle = v === 0 ? "rgba(229,231,235,0.5)" : `rgba(232,83,26,${alpha})`;
        ctx.beginPath();
        ctx.roundRect(x + 1, y + 1, cellW - 2, cellH - 2, 2);
        ctx.fill();
      });
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(days[d], 20, d * cellH + cellH / 2 + 3);
    });
    // Hour labels
    [0, 6, 12, 18, 23].forEach(h => {
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${h}时`, 24 + h * cellW + cellW / 2, H - 2);
    });
  }, []);

  useEffect(() => {
    drawTrend();
    drawDonut();
    drawHeatmap();
  }, [activeView, drawTrend, drawDonut, drawHeatmap]);

  const generateReport = () => {
    setGenerating(true);
    setReport("");
    let i = 0;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (i < aiReportText.length) {
        setReport(aiReportText.slice(0, i + 1));
        i += 3;
      } else {
        setReport(aiReportText);
        if (timerRef.current) clearInterval(timerRef.current);
        setGenerating(false);
      }
    }, 15);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  return (
    <div className="space-y-4">
      {/* AI Overview Cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "AI识别告警", value: total, sub: "今日自动识别", icon: "ri-robot-2-line", color: "text-[#E8531A]", bg: "bg-orange-50" },
          { label: "高风险用户", value: riskUsers.filter(r => r.level === "高危").length, sub: "需立即处置", icon: "ri-user-unfollow-line", color: "text-red-600", bg: "bg-red-50" },
          { label: "AI预测风险", value: "12%", sub: "未来7天泄漏概率", icon: "ri-line-chart-line", color: "text-amber-600", bg: "bg-amber-50" },
          { label: "处置建议", value: 4, sub: "条AI优化建议", icon: "ri-lightbulb-line", color: "text-teal-600", bg: "bg-teal-50" },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white">
              <i className={`${s.icon} text-xl ${s.color}`}></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-gray-400">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: "overview", label: "AI概览" },
          { key: "category", label: "分类分析" },
          { key: "trend", label: "趋势图表" },
          { key: "risk", label: "风险用户" },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setActiveView(v.key as typeof activeView)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeView === v.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeView === "overview" && (
        <div className="grid grid-cols-3 gap-4">
          {/* Donut chart */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">告警级别分布</h3>
            <div className="flex items-center gap-4">
              <canvas ref={donutCanvasRef} width={100} height={100} className="flex-shrink-0" />
              <div className="space-y-2 flex-1">
                {[
                  { label: "紧急", count: 1, color: "bg-red-500" },
                  { label: "严重", count: 2, color: "bg-orange-500" },
                  { label: "警告", count: 3, color: "bg-amber-500" },
                  { label: "提示", count: 2, color: "bg-teal-500" },
                ].map((l, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${l.color} flex-shrink-0`}></div>
                    <span className="text-xs text-gray-500 flex-1">{l.label}</span>
                    <span className="text-xs font-bold text-gray-700">{l.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Priority actions */}
          <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">AI处置优先级建议</h3>
            <div className="space-y-2">
              {[
                { priority: "P0", label: "立即处置", desc: "海淀区燃气泄漏告警 - 浓度超标2.5倍", color: "bg-red-500", text: "text-red-600", bg: "bg-red-50", time: "0分钟内" },
                { priority: "P1", label: "2小时内", desc: "3条未处理告警需完成响应", color: "bg-orange-500", text: "text-orange-600", bg: "bg-orange-50", time: "2小时" },
                { priority: "P2", label: "今日内", desc: "欠费用户短信催缴，预计回收¥2,400", color: "bg-amber-500", text: "text-amber-600", bg: "bg-amber-50", time: "今日" },
                { priority: "P3", label: "本周内", desc: "安检逾期用户安排上门安检", color: "bg-teal-500", text: "text-teal-600", bg: "bg-teal-50", time: "7天" },
              ].map((p, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 ${p.bg} rounded-xl`}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${p.color} whitespace-nowrap`}>{p.priority}</span>
                  <div className="flex-1">
                    <span className={`text-xs font-semibold ${p.text}`}>{p.label}</span>
                    <span className="text-xs text-gray-600 ml-2">{p.desc}</span>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{p.time}</span>
                  <button className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">处理</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Category Analysis */}
      {activeView === "category" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">告警类型分布（AI识别）</h3>
            <div className="space-y-2.5">
              {typeDistribution.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                  <span className="text-xs text-gray-600 w-20 flex-shrink-0">{item.type}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{ width: `${item.pct}%`, backgroundColor: item.color }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-6 text-right">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">告警热力图（近7日×24小时）</h3>
            <canvas ref={heatmapCanvasRef} width={380} height={120} className="w-full" />
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-gray-400">低</span>
              <div className="flex gap-1">
                {[0.05, 0.3, 0.6, 0.9].map((a, i) => (
                  <div key={i} className="w-4 h-3 rounded-sm" style={{ backgroundColor: `rgba(232,83,26,${a})` }}></div>
                ))}
              </div>
              <span className="text-xs text-gray-400">高</span>
            </div>
          </div>
        </div>
      )}

      {/* Trend Analysis */}
      {activeView === "trend" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">近7日告警趋势（AI分析）</h3>
              <div className="flex items-center gap-3">
                {[
                  { label: "紧急", color: "bg-red-500" },
                  { label: "严重", color: "bg-orange-500" },
                  { label: "警告", color: "bg-amber-400" },
                  { label: "提示", color: "bg-teal-400" },
                ].map((l, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className={`w-3 h-3 rounded-sm ${l.color}`}></div>
                    <span className="text-xs text-gray-500">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <canvas ref={trendCanvasRef} width={700} height={180} className="w-full" />
            <div className="mt-4 p-3 bg-orange-50 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-robot-2-line text-[#E8531A] text-sm"></i>
                <span className="text-xs font-semibold text-[#E8531A]">AI趋势洞察</span>
              </div>
              <p className="text-xs text-gray-600">近7日告警总量呈上升趋势，今日达到峰值8条。紧急告警集中在4/5和4/10，与季节性管道压力变化相关。建议加强4月中旬的设备巡检频率，预计可降低告警发生率约30%。</p>
            </div>
          </div>

          {/* Prediction */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">AI预测 - 未来7天告警风险</h3>
            <div className="grid grid-cols-7 gap-2">
              {[
                { day: "4/11", risk: 35, level: "低" },
                { day: "4/12", risk: 42, level: "低" },
                { day: "4/13", risk: 58, level: "中" },
                { day: "4/14", risk: 72, level: "中" },
                { day: "4/15", risk: 45, level: "低" },
                { day: "4/16", risk: 38, level: "低" },
                { day: "4/17", risk: 65, level: "中" },
              ].map((d, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs text-gray-400 mb-2">{d.day}</div>
                  <div className="relative h-20 bg-gray-50 rounded-lg overflow-hidden flex items-end">
                    <div
                      className="w-full rounded-t transition-all"
                      style={{
                        height: `${d.risk}%`,
                        backgroundColor: d.risk >= 70 ? "#f97316" : d.risk >= 50 ? "#f59e0b" : "#22c55e",
                        opacity: 0.7,
                      }}
                    />
                  </div>
                  <div className={`text-xs font-medium mt-1 ${d.risk >= 70 ? "text-orange-600" : d.risk >= 50 ? "text-amber-600" : "text-green-600"}`}>{d.risk}%</div>
                  <div className="text-[10px] text-gray-400">{d.level}风险</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Risk Users */}
      {activeView === "risk" && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">AI风险用户评估</h3>
            <button className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1.5 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap">
              <i className="ri-send-plane-line mr-1"></i>批量催缴
            </button>
          </div>
          <div className="space-y-3">
            {riskUsers.map((user, i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="w-10 h-10 rounded-full bg-[#E8531A] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">{user.name[0]}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-800">{user.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      user.level === "高危" ? "bg-red-100 text-red-600" :
                      user.level === "中危" ? "bg-orange-100 text-orange-600" :
                      user.level === "低危" ? "bg-amber-100 text-amber-600" :
                      "bg-teal-100 text-teal-600"
                    }`}>{user.level}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {user.issues.map((issue, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-500">{issue}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs text-gray-400 mb-1">AI风险评分</div>
                  <div className={`text-xl font-bold ${user.risk >= 80 ? "text-red-600" : user.risk >= 60 ? "text-orange-600" : user.risk >= 40 ? "text-amber-600" : "text-teal-600"}`}>{user.risk}</div>
                  <div className="w-20 bg-gray-200 rounded-full h-1.5 mt-1">
                    <div className={`h-1.5 rounded-full ${user.risk >= 80 ? "bg-red-500" : user.risk >= 60 ? "bg-orange-500" : user.risk >= 40 ? "bg-amber-500" : "bg-teal-500"}`} style={{ width: `${user.risk}%` }} />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button className="text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">处置</button>
                  <button className="text-xs border border-gray-200 text-gray-500 px-3 py-1.5 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap">详情</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Report Generator */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
              <i className="ri-robot-2-line text-[#E8531A] text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">AI智能分析报告</h3>
              <p className="text-xs text-gray-400">打字式逐字生成，支持自定义分析要求</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={generateReport}
              disabled={generating}
              className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
            >
              {generating ? <><i className="ri-loader-4-line animate-spin"></i>生成中...</> : <><i className="ri-robot-2-line"></i>AI生成报告</>}
            </button>
            {report && (
              <>
                <button onClick={() => setExportModal(true)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-file-pdf-line text-red-500"></i>导出PDF
                </button>
                <button onClick={() => setExportModal(true)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-file-word-line text-blue-500"></i>导出Word
                </button>
              </>
            )}
          </div>
        </div>
        <div className="mb-3 flex gap-2">
          <input
            value={customPrompt}
            onChange={e => setCustomPrompt(e.target.value)}
            placeholder="自定义分析要求（如：重点分析泄漏类告警的处置效率...）"
            className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
          />
          <button
            onClick={generateReport}
            disabled={generating}
            className="px-3 py-2 border border-[#E8531A] text-[#E8531A] text-xs rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            自定义生成
          </button>
        </div>
        {report ? (
          <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-700 leading-relaxed whitespace-pre-line font-mono max-h-80 overflow-y-auto">
            {report}
            {generating && <span className="animate-pulse text-[#E8531A]">|</span>}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-robot-2-line text-[#E8531A] text-2xl"></i>
            </div>
            <p className="text-sm text-gray-400">点击「AI生成报告」，自动分析告警数据并生成智能报告</p>
            <p className="text-xs text-gray-300 mt-1">支持自定义分析要求，打字式逐字生成</p>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setExportModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[420px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">导出AI分析报告</h3>
              <button onClick={() => setExportModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: "PDF文档", icon: "ri-file-pdf-2-line", color: "text-red-500", desc: "含图表" },
                { label: "Word文档", icon: "ri-file-word-line", color: "text-blue-500", desc: "可编辑" },
                { label: "Excel表格", icon: "ri-file-excel-2-line", color: "text-green-600", desc: "数据表" },
              ].map((fmt, i) => (
                <button key={i} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-100 hover:border-[#E8531A] cursor-pointer transition-colors" onClick={() => setExportModal(false)}>
                  <i className={`${fmt.icon} text-2xl ${fmt.color}`}></i>
                  <div className="text-xs font-semibold text-gray-700">{fmt.label}</div>
                  <div className="text-[10px] text-gray-400">{fmt.desc}</div>
                </button>
              ))}
            </div>
            <button onClick={() => setExportModal(false)} className="w-full border border-gray-200 text-gray-500 py-2.5 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
          </div>
        </div>
      )}
    </div>
  );
}
